import React, { FC, useMemo } from "react";
import { View } from "react-native";
import Animated, { useAnimatedStyle, useSharedValue, withDelay, withSpring } from "react-native-reanimated";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Text } from "@lookiero/sty-psp-ui";
import { useStaticInfo } from "../../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages, PREFIX } from "../../../../i18n/i18n";
import { style as nextBestActionSuccessStyle } from "./NextBestActionSuccess.style";
import { Tick } from "./Tick";

const ANIMATION_TRANSLATE_Y = 30;

interface NextBestActionSuccessProps {
  readonly onNavigateToStyleProfile: () => void;
}

const NextBestActionSuccess: FC<NextBestActionSuccessProps> = ({ onNavigateToStyleProfile }) => {
  const {
    customer: { segment },
  } = useStaticInfo();

  const title = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.NEXT_BEST_ACTION_SUCCESS_TITLE,
    prefix: PREFIX,
    values: { segment: segment.toLowerCase() },
  });
  const description = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.NEXT_BEST_ACTION_SUCCESS_DESCRIPTION,
    prefix: PREFIX,
    values: { segment: segment.toLowerCase() },
  });
  const cta = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.NEXT_BEST_ACTION_SUCCESS_CTA,
    prefix: PREFIX,
    values: { segment: segment.toLowerCase() },
  });

  const style = useMemo(() => nextBestActionSuccessStyle(), []);

  const textAnimation = useSharedValue(0);
  textAnimation.value = withSpring(1);
  const textAnimatedStyles = useAnimatedStyle(
    () => ({
      opacity: textAnimation.value,
      transform: [{ translateY: ANIMATION_TRANSLATE_Y * (1 - textAnimation.value) }],
    }),
    [textAnimation],
  );

  const ctaAnimation = useSharedValue(0);
  ctaAnimation.value = withDelay(100, withSpring(1));
  const ctaAnimatedStyles = useAnimatedStyle(
    () => ({
      opacity: ctaAnimation.value,
      transform: [{ translateY: ANIMATION_TRANSLATE_Y * (1 - ctaAnimation.value) }],
    }),
    [ctaAnimation],
  );

  return (
    <View style={style.container} testID="next-best-action-success">
      <Tick />

      <Animated.View style={[style.textContainer, textAnimatedStyles]}>
        <Text level={3} style={style.title} variant="heading">
          {title}
        </Text>
        <Text level={1} style={style.description} variant="detail">
          {description}
        </Text>
      </Animated.View>
      <Animated.View style={[style.ctaContainer, ctaAnimatedStyles]}>
        <Button
          size="small"
          testID="next-best-action-success-cta"
          variant="secondary"
          onPress={onNavigateToStyleProfile}
        >
          {cta}
        </Button>
      </Animated.View>
    </View>
  );
};

export { NextBestActionSuccess };
